/* App.js */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, ExternalLink, Code2, Send, ChevronDown, Menu, X } from 'lucide-react';
import './App.css';

// --- DATA ---
const skills = {
  frontend: ["JavaScript (ES6+)", "React.js", "HTML5 / CSS3", "JavaFX"],
  backend: ["Java","Node.js", "Go (Golang)", "Python","PostgreSQL", "Redis"],
  core: ["Machine Learning", "Streamlit", "Docker", "Git / GitHub", "GCP"]
};

const certifications = [
  {
    id: "CERT_JB_01",
    title: "Java Foundations Professional Certificate",
    issuer: "JetBrains"
  },
  {
    id: "CERT_GSSOC_25",
    title: "GirlScript Summer of Code 2025 Certification",
    issuer: "GirlScript Foundation"
  },
  {
    id: "CERT_PM_01",
    title: "Postman API Fundamentals Student Expert",
    issuer: "Postman"
  }
];

const projects = [
  {
    title: "ChloroCast (SDGP)",
    meta: "[type: Machine Learning] [status: Active]",
    desc: "A predictive model for the National Water Board. Engineered a chlorine demand forecasting system to optimize public resource management.",
    tech: ["Python", "scikit-learn", "Pandas", "Streamlit"],
    link: "https://github.com/Kalika-Jay/ChloroCast",
    image: require('./assets/chlorocast.png') 
  },
  {
    title: "CVAnalyst",
    meta: "[type: NLP Application] [status: Deployed]",
    desc: "An automated resume screening tool. Utilizes TF-IDF similarity scoring to parse PDF resumes and algorithmically match candidates against job descriptions.",
    tech: ["Python", "NLP", "TF-IDF", "Streamlit"],
    link: "https://github.com/Kalika-Jay/CVAnalyst",
    image: require('./assets/cvanalyst.png')
  },
  {
    title: "Kacha-Kacha",
    meta: "[type: Full-Stack WebSockets] [status: Complete]",
    desc: "Engineered a lightning-fast, real-time messaging architecture. Backed by a high-concurrency Go WebSocket server and a snappy React frontend.",
    tech: ["Go (Golang)", "React", "Redis", "Asgardeo"],
    link: "https://github.com/Kalika-Jay/Kacha-Kacha",
    image: require('./assets/kacha.png')
  },
  {
    title: "SmartLibrary",
    meta: "[type: Desktop Application] [status: Complete]",
    desc: "A comprehensive Library Management System emphasizing clean software design. Built entirely on a strict MVC architecture for maximum maintainability.",
    tech: ["JavaFX", "PostgreSQL", "MVC"],
    link: "https://github.com/Kalika-Jay/Smart-Library-Management-System",
    image: require('./assets/library.png')
  }
];

const achievements = [
  {
    id: "EVT_089",
    title: "Hult Prize Finalist 2026",
    desc: "Pitched 'ChloroCast' and successfully secured a spot in the Top 12 out of 240 competing teams at the IIT Hult Prize On-Campus Competition.",
  },
  {
    id: "EVT_042",
    title: "IEEE Xtreme (18.0 & 19.0)",
    desc: "Endured a grueling 24-hour global programming hackathon, tackling advanced algorithmic and data structure challenges.",
  },
  {
    id: "EVT_017",
    title: "Idealize 2025 Team Lead",
    desc: "Spearheaded the development of 'TravaGo', a smart travel companion application featuring ML-based intelligent trip routing.",
  },
  {
    id: "EVT_012",
    title: "GirlScript Summer of Code 2025",
    desc: "Contributed to the open-source project 'notesVault'. Improved UI components and optimized JSON-based resource handling.",
  }
];

// --- ANIMATION VARIANTS ---
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export default function App() {
  const [typedText, setTypedText] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const fullText = "Software Engineering Undergrad";

  // Terminal Typing Effect
  useEffect(() => {
    let i = 0;
    setTypedText(""); 
    
    const typingInterval = setInterval(() => {
      i++;
      setTypedText(fullText.slice(0, i)); 
      
      if (i >= fullText.length) {
        clearInterval(typingInterval);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, [fullText]);

  const scrollTo = (e, id) => {
    e.preventDefault();
    setIsMobileMenuOpen(false); // Close mobile menu on click
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const isTypingComplete = typedText === fullText;

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Navbar */}
      <nav className="navbar">
        <div className="nav-container">
          <a href="#home" className="nav-logo" onClick={(e) => scrollTo(e, 'home')}>
            Kalika_Jay<span className="cursor-blink" style={{width: '8px', height: '1em', marginLeft: '2px'}}></span>
          </a>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-btn" 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          <div className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}>
            <a href="#skills" onClick={(e) => scrollTo(e, 'skills')} className="nav-link">Skills</a>
            <a href="#education" onClick={(e) => scrollTo(e, 'education')} className="nav-link">Education</a>
            <a href="#projects" onClick={(e) => scrollTo(e, 'projects')} className="nav-link">Projects</a>
            <a href="#certs" onClick={(e) => scrollTo(e, 'certs')} className="nav-link">Certs</a>
            <a href="#trace" onClick={(e) => scrollTo(e, 'trace')} className="nav-link">Logs</a>
            <a href="#contact" onClick={(e) => scrollTo(e, 'contact')} className="nav-link">Contact</a>
          </div>
        </div>
      </nav>

      <main className="container">
        
        {/* HERO SECTION */}
        <section id="home" className="section" style={{ minHeight: '90vh', position: 'relative' }}>
          <motion.div 
            className="hero-raw-cli"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="prompt-line">
              <span className="prompt-user">system@kalika</span>:<span className="prompt-path">/boot</span><span className="prompt-char">#</span>
              <span className="cmd-text"> ./init_profile.sh</span>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="hero-intro"
            >
              <h1 className="hero-name">Kalika</h1>
              <h1 className="hero-name">Jayasinghe_Arachchi</h1>
              <h2 className="hero-role">
                <span className="cli-accent">&gt; </span>
                {typedText}
                {!isTypingComplete && <span className="cursor-blink"></span>}
              </h2>
            </motion.div>

            {isTypingComplete && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ delay: 0.2 }}
                className="hero-sys-info"
              >
                <div className="sys-line">
                  <span className="sys-key">LOCATION   :</span> 
                  <span className="sys-val">Colombo, Sri Lanka</span>
                </div>
                <div className="sys-line">
                  <span className="sys-key">EDUCATION  :</span> 
                  <span className="sys-val">IIT (University of Westminster)</span>
                </div>
                <div className="sys-line">
                  <span className="sys-key">FOCUS_AREA :</span> 
                  <span className="sys-val">Full-Stack , Machine Learning</span>
                </div>
                <div className="sys-line mt-2">
                  <span className="sys-key">STATUS     :</span> 
                  <span className="sys-val highlight">Seeking Software Engineering Internships</span>
                </div>

                <div className="hero-actions">
                  <a href="#projects" onClick={(e) => scrollTo(e, 'projects')} className="cmd-btn">
                    ./view_projects
                  </a>
                  <a href="#contact" onClick={(e) => scrollTo(e, 'contact')} className="cmd-btn primary">
                    ./contact_me.sh
                  </a>
                </div>
              </motion.div>
            )}
          </motion.div>
          
          <div className="scroll-indicator">
            <p style={{marginBottom: '0.5rem'}}>scroll_down</p>
            <ChevronDown size={20} />
          </div>
        </section>

        {/* SKILLS SECTION */}
        <section id="skills" className="section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="section-header">
            <h2 className="section-title">Environment_Variables</h2>
          </motion.div>

          <motion.div 
            className="blocks-grid"
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true, margin: "-100px" }} 
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="cli-block">
              <div className="cli-block-header">{'// frontend_deps.json'}</div>
              <div className="cli-block-list">
                {skills.frontend.map((skill, i) => (
                  <div key={i} className="cli-block-item">{skill}</div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="cli-block">
              <div className="cli-block-header">{'// backend_services.yml'}</div>
              <div className="cli-block-list">
                {skills.backend.map((skill, i) => (
                  <div key={i} className="cli-block-item">{skill}</div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="cli-block">
              <div className="cli-block-header">{'// core_infrastructure.cfg'}</div>
              <div className="cli-block-list">
                {skills.core.map((skill, i) => (
                  <div key={i} className="cli-block-item">{skill}</div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* EDUCATION SECTION */}
        <section id="education" className="section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="section-header">
            <h2 className="section-title">Academic_Trace</h2>
          </motion.div>

          <motion.div 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true, margin: "-100px" }} 
            variants={staggerContainer}
            style={{ maxWidth: '800px' }}
          >
            <motion.div variants={fadeUp} className="cli-trace-block">
              <div className="cli-trace-header">
                <span className="cli-trace-tag">[]</span> 
                <span>BEng. Software Engineering</span>
              </div>
              <div className="cli-trace-body">
                <div className="cli-trace-line">
                  <span className="cli-trace-key">INST:</span>
                  <span className="cli-trace-val">University of Westminster (IIT Sri Lanka)</span>
                </div>
                <div className="cli-trace-line">
                  <span className="cli-trace-key">TIME:</span>
                  <span className="cli-trace-val">2024 - PRESENT</span>
                </div>
                <div className="cli-trace-line">
                  <span className="cli-trace-key">STAT:</span>
                  <span className="cli-status active">IN_PROGRESS</span>
                </div>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="cli-trace-block">
              <div className="cli-trace-header">
                <span className="cli-trace-tag">[]</span> 
                <span>Secondary Education (GCE A/L & O/L)</span>
              </div>
              <div className="cli-trace-body">
                <div className="cli-trace-line">
                  <span className="cli-trace-key">INST:</span>
                  <span className="cli-trace-val">Ananda College, Colombo 10</span>
                </div>
                <div className="cli-trace-line">
                  <span className="cli-trace-key">TIME:</span>
                  <span className="cli-trace-val">2014 - 2023</span>
                </div>
                <div className="cli-trace-line">
                  <span className="cli-trace-key">STAT:</span>
                  <span className="cli-status complete">COMPLETED</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* PROJECTS SECTION */}
        <section id="projects" className="section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="section-header">
            <h2 className="section-title">Deployed_Systems</h2>
          </motion.div>

          <div className="projects-list">
            {projects.map((project, idx) => (
              <motion.div 
                key={idx}
                className="project-feature"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUp}
              >
                <div className="project-image-wrapper">
                  {project.image ? (
                    <img src={project.image} alt={project.title} className="project-img" 
                      onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                    />
                  ) : null}
                  <div className="img-placeholder" style={{ display: project.image ? 'none' : 'flex' }}>
                    <Code2 size={48} opacity={0.5} />
                  </div>
                </div>
                
                <div className="project-info">
                  <div className="project-meta">{project.meta}</div>
                  <h3 className="project-name">{project.title}</h3>
                  <p className="project-desc">{project.desc}</p>
                  
                  <div className="project-stack">
                    {project.tech.map((t, i) => (
                      <span key={i}>[{t}]</span>
                    ))}
                  </div>

                  <div className="project-links">
                    <a href={project.link} target="_blank" rel="noreferrer" className="icon-link">
                      <Github size={24} />
                    </a>
                    <a href={project.link} target="_blank" rel="noreferrer" className="icon-link">
                      <ExternalLink size={24} />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CERTIFICATIONS SECTION */}
        <section id="certs" className="section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="section-header">
            <h2 className="section-title">Verified_Credentials</h2>
          </motion.div>

          <motion.div 
            className="blocks-grid"
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true, margin: "-100px" }} 
            variants={staggerContainer}
          >
            {certifications.map((cert, i) => (
              <motion.div key={i} variants={fadeUp} className="cli-block" style={{ gap: '1rem', justifyContent: 'center' }}>
                <div className="cli-block-header" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
                  {`// credential_id: ${cert.id}`}
                </div>
                <div>
                  <h3 style={{ color: 'var(--text-main)', fontSize: '1.125rem', margin: '0 0 0.5rem 0', fontFamily: 'var(--font-sans)', fontWeight: '600' }}>
                    {cert.title}
                  </h3>
                  <div style={{ color: 'var(--cli-accent)', fontSize: '0.875rem' }}>
                    issuer: {cert.issuer}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* EVENT TRACE SECTION (Logs) */}
        <section id="trace" className="section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="section-header">
            <h2 className="section-title">Event_Trace</h2>
          </motion.div>

          <motion.div 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true, margin: "-100px" }} 
            variants={staggerContainer}
            style={{ maxWidth: '800px' }}
          >
            {achievements.map((ach, idx) => (
              <motion.div key={idx} variants={fadeUp} className="cli-log-entry">
                <div className="cli-log-meta">
                  <span>[{ach.id}]</span>
                  <span className="cli-log-title">{ach.title}</span>
                </div>
                <div className="cli-log-desc">
                  {ach.desc}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* CONTACT SECTION */}
        <section id="contact" className="section" style={{ minHeight: '60vh' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="section-header text-center" style={{ textAlign: 'center' }}>
            <h2 className="section-title" style={{ justifyContent: 'center' }}>Init_Connection</h2>
            <p style={{ color: 'var(--text-muted)', marginTop: '1.5rem', fontSize: '1.125rem' }}>
              Seeking internship opportunities. Execute the script below to establish a direct comms link.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="terminal-window contact-terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <div className="dot red"></div>
                <div className="dot yellow"></div>
                <div className="dot green"></div>
              </div>
              <div className="terminal-title">contact.sh</div>
            </div>
            <div className="terminal-body contact-body">
              
              <div className="cli-input-row">
                <span className="cli-prompt">Name:~$</span>
                <input type="text" className="cli-input" placeholder="enter_your_name..." />
              </div>
              
              <div className="cli-input-row">
                <span className="cli-prompt">Email:~$</span>
                <input type="email" className="cli-input" placeholder="enter_your_email..." />
              </div>
              
              <div className="cli-input-row textarea">
                <span className="cli-prompt">Msg:~$</span>
                <textarea className="cli-input cli-textarea" placeholder="type_your_message..."></textarea>
              </div>

              <button className="cli-submit-btn">
                ./send_message <Send size={16} />
              </button>
              
            </div>
          </motion.div>
          
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '4rem' }}>
            <a href="https://github.com/Kalika-Jay" target="_blank" rel="noreferrer" style={{ color: 'var(--text-muted)' }}>
              <Github size={32} />
            </a>
            <a href="https://linkedin.com/in/kalika-jay" target="_blank" rel="noreferrer" style={{ color: 'var(--text-muted)' }}>
              <Linkedin size={32} />
            </a>
          </motion.div>
        </section>

      </main>

      <footer className="footer">
        <p>System Designed & Built by Kalika Jayasinghe Arachchi © 2026</p>
      </footer>
    </>
  );
}